export interface UserProfile {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  fullName: string | null
  phone: string | null
  avatarUrl: string | null
  storeCredit: number
  isVerified: boolean
  createdAt: string
}

interface AuthResponse {
  token: string
  user: UserProfile
}

interface LoginRequest {
  email: string
  password: string
}

interface RegisterRequest {
  email: string
  password: string
  firstName?: string
  lastName?: string
  /** Cloudflare Turnstile token, required when backend has TURNSTILE_SECRET_KEY set. */
  captchaToken?: string
}

export const useAuth = () => {
  const user = useState<UserProfile | null>('user', () => null)
  const isLoggedIn = computed(() => !!user.value)
  const authLoading = ref(false)

  // Cookie is set/cleared exclusively by the backend via Set-Cookie (HttpOnly,
  // Secure, Domain-scoped). We only READ it — never write — so the canonical
  // backend-issued cookie is the single source of truth. On SSR useCookie
  // reads the request Cookie header (HttpOnly is fine). On the client the
  // HttpOnly cookie is invisible to JS but the browser still sends it with
  // credentials:'include'.
  const tokenCookie = useCookie('_authentication')

  const login = async (email: string, password: string) => {
    authLoading.value = true
    try {
      const data = await apiPost<AuthResponse>('/api/app/v1/auth/login', { email, password } as LoginRequest)
      user.value = data.user
      return data.user
    } finally {
      authLoading.value = false
    }
  }

  const register = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    captchaToken?: string
  ) => {
    authLoading.value = true
    try {
      const data = await apiPost<AuthResponse>('/api/app/v1/auth/register', {
        email,
        password,
        firstName,
        lastName,
        captchaToken
      } as RegisterRequest)
      user.value = data.user
      return data.user
    } finally {
      authLoading.value = false
    }
  }

  const fetchProfile = async () => {
    try {
      // SSR fetch is server-to-server, so credentials:'include' has no effect.
      // Forward the original browser Cookie header explicitly. Done here (in
      // a place with guaranteed Nuxt context) as a belt-and-suspenders for
      // the api.ts interceptor in case the latter loses context.
      const opts: { headers?: Record<string, string> } = {}
      if (import.meta.server) {
        const headers = useRequestHeaders(['cookie'])
        if (headers.cookie) opts.headers = { Cookie: headers.cookie }
      }
      const data = await apiGet<UserProfile>('/api/app/v1/user/profile', opts)
      user.value = data
      return data
    } catch (error: any) {
      // Only clear local state on auth errors (401). Network/server errors
      // must NOT destroy the session.
      if (error?.message === 'Authentication required' || error?.statusCode === 401) {
        user.value = null
      }
      return null
    }
  }

  const logout = async () => {
    try {
      await apiPost('/api/app/v1/auth/logout')
    } catch {
      // Best-effort: clear local state regardless of network outcome.
    }
    user.value = null
  }

  // Restore user state from cookie on both server and client.
  // SSR: useCookie reads the request Cookie header (HttpOnly is fine).
  // Client: useCookie can't see HttpOnly cookies, so fall back to a profile
  // fetch — credentials:'include' lets the browser send the cookie.
  // Non-HttpOnly hint cookie the backend sets alongside the HttpOnly auth cookie
  // (see AppAuthInterceptor / UserController). Readable by JS, so on the client
  // we only fetch the profile when a session likely exists — anonymous visitors
  // (and crawlers) skip the call and its expected 401, which the browser would
  // otherwise log as a console error.
  const loginHint = useCookie('_auth_hint')

  const initAuth = async () => {
    if (user.value) return
    if (import.meta.server) {
      if (tokenCookie.value) await fetchProfile()
    } else if (loginHint.value) {
      await fetchProfile()
    }
  }

  return {
    user,
    isLoggedIn,
    authLoading,
    login,
    register,
    logout,
    fetchProfile,
    initAuth,
  }
}
